/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import { getIdentities, searchIdentities } from 'src/azure/services/get-board-users';
import { WorkItemAssignedToInputDataType, WorkItemIsAssignedToErrorType, WorkItemIsAssignedToTriggerType } from '../types';
import { stringRequired } from '../../utils/validations';
import { IIdentity } from 'azure-devops-extension-api/Identities';

export const inititalAssignedToForm: WorkItemIsAssignedToTriggerType = {
	identity: '',
	identityOptions: [],
	loading: true,
	selectedOption: '',
};

export default function useAssignedToForm() {
	const searchIdentityOptions = useCallback(async (text: string) => {
		const identitiesResults = await searchIdentities(text);
		return identitiesResults.map((item: any) => ({
			label: `${item.displayName}${item.mail ? ` <${item.mail}>` : ''}`,
			value: item.localId
		}));
	}, []);

	const getIdentityListOptions = useCallback(async (): Promise<Array<{label: string; value: string}>> => {
		const users = await getIdentities();
    
		return users!.map((item: any) => ({
			label: `${item.displayName}${` <${item.mail}>`}`,
			value: item.localId
		}));
	}, []);

	const validate = useCallback((values: {
		identity: string
	}): {
		hasErrors: () => boolean;
		errors: WorkItemIsAssignedToErrorType
	} => {
		let hasErrors = false;
		const validations = {
			identity: [
				stringRequired(values.identity, 'This field is required')
			]
		};

		const getError = (validation: Array<string | undefined>) => {
			const foundError = validation.find(item => item !== undefined) || null;
			if (foundError) {
				hasErrors = true;
			}
			
			return foundError || null;
		};

		return {
			hasErrors: () => hasErrors,
			errors: {
				identity: getError(validations.identity),
			}
		};
	}, []);

	const getValues = useCallback((data: WorkItemIsAssignedToTriggerType) => {
		return {
			assignedTo: data.selectedOption
		};
	}, []);

	const getIdentitiesByIds = useCallback(async (ids: Array<string>) => {
		const results = await Promise.all(ids.map(async (id) => {
			const identities = await searchIdentities(id, { withGroup: true, queryTypeHint: 'uid' });
			const found: any = identities.find((identity) => (identity as IIdentity & { localId: string }).localId === id);
			return {
				label: `${found?.displayName} <${found?.mail}>`,
				value: found?.localId
			};
		}));

		return results;
	}, []);

	const loadValues = useCallback(async (input: {
		data: WorkItemAssignedToInputDataType,
		organizationName: string;
	}): Promise<WorkItemIsAssignedToTriggerType> => {
		const options = await getIdentityListOptions();

		const foundIdentities = await getIdentitiesByIds([input.data.assignedTo]); 

		foundIdentities.forEach((idItem) => {
			if (!options.some(item => item.value === idItem.value)) {
				options.push(idItem);
			}
		});

		return {
			identity: options.find(item => item.value === input.data.assignedTo)?.label || '',
			loading: false,
			identityOptions: options,
			selectedOption: input.data.assignedTo,
		};
	}, []);

	return {
		searchIdentityOptions,
		getIdentityListOptions,
		validate,
		getValues,
		loadValues
	};
}
